import { createReducer, type Draft, type Reducer } from "@reduxjs/toolkit";
import arrayMove from "array-move";
import { omit } from "es-toolkit";

import { assert } from "../../../asserts/index.ts";
import type {
  AddConditionAction,
  ConditionBuild,
  ConditionSlotId,
  MoveConditionAction,
  RemoveConditionAction,
  RemoveNodeAction,
  RemoveResponseAction,
  RemoveTriggerAction,
  ReorderConditionAction,
  UpdateConditionConfigAction,
} from "../../../schemas/index.ts";
import type { ConditionsState } from "../../../schemas/nodeDesigns/conditionDesignsStateSchema.ts";
import { extractNodeValueRefIds, resetNodeValueRefs } from "../../../shared/index.ts";
import { CONDITION_DEFAULT_SLOT_NAME, CONDITION_ROOT_SLOT_ID } from "../../constants.ts";
import { InvalidActionError } from "../../InvalidActionError.ts";
import { removeNodeAction } from "../actions.ts";
import type { Mutable } from "../shared.ts";
import {
  addConditionAction,
  moveConditionAction,
  removeConditionAction,
  removeResponseAction,
  removeTriggerAction,
  reorderConditionAction,
  updateConditionAction,
} from "./triggerActions.ts";
import type { TriggersReducerOptions } from "./triggersReducerOptions.ts";

export const conditionsInitialState: ConditionsState = {
  byId: {},
  parentIdByChildId: {},
  idByResponseId: {},
  idByTriggerId: {},
  slotsByParentId: {},
  responseIdByRootConditionId: {},
  triggerIdByRootConditionId: {},
};

export type ConditionAction =
  | AddConditionAction
  | RemoveConditionAction
  | ReorderConditionAction
  | UpdateConditionConfigAction
  | MoveConditionAction
  | RemoveResponseAction
  | RemoveTriggerAction
  | RemoveNodeAction;

export const conditionsReducerCreate = ({ isNodeIdValid }: TriggersReducerOptions = {}): Reducer<
  ConditionsState,
  ConditionAction
> => {
  const validateAddConditionCreate =
    (state: ConditionsState, action: AddConditionAction) =>
    <T extends Pick<ConditionBuild, "id" | "nodeId" | "config">>(condition: T) => {
      const {
        payload: { to },
      } = action;
      if ("responseId" in to && to.responseId in state.idByResponseId) {
        throw new InvalidActionError(action, `Response with id ${to.responseId} already has a condition.`);
      } else if ("triggerId" in to && to.triggerId in state.idByTriggerId) {
        throw new InvalidActionError(action, `Trigger with id ${to.triggerId} already has a condition.`);
      } else if ("slotId" in to) {
        const {
          slotId: { parentId },
        } = to;
        if (parentId === condition.id) {
          throw new InvalidActionError(action, `Cannot add a condition to itself.`);
        }
        if (!(parentId in state.byId)) {
          throw new InvalidActionError(action, `Parent condition with id: ${parentId} not found.`);
        }
      }

      if (condition.id in state.byId) {
        throw new InvalidActionError(action, `Condition already exists with id: ${condition.id}.`);
      }
      if (!isNodeIdValid) return condition;
      if (condition.nodeId && !isNodeIdValid(condition.nodeId)) {
        throw new InvalidActionError(
          action,
          `Condition nodeId not found: ${condition.nodeId} on condition ${condition.id}.`,
        );
      }
      const invalidValueRefIds = extractNodeValueRefIds(condition.config).filter((id) => !isNodeIdValid(id));
      if (invalidValueRefIds.length > 0) {
        throw new InvalidActionError(
          action,
          `Invalid value ref(s): ${invalidValueRefIds.join(",")} on condition ${condition.id}.`,
        );
      }
      return condition;
    };

  return createReducer(conditionsInitialState, (builder) => {
    const findSlotIdAndIndexCreate =
      (state: Draft<ConditionsState>) =>
      (conditionId: number): { slotId: ConditionSlotId; index?: number } | undefined => {
        const { slotsByParentId, parentIdByChildId } = state;
        const parentId = parentIdByChildId[conditionId];
        if (!parentId) {
          return undefined;
        }
        const slotsForParent = slotsByParentId[parentId] ?? {};
        for (const [name, idOrIds] of Object.entries(slotsForParent)) {
          if (Array.isArray(idOrIds)) {
            const index = idOrIds.indexOf(conditionId);
            if (index >= 0) {
              return {
                slotId: { name, parentId },
                index,
              };
            }
          } else if (idOrIds === conditionId) {
            return {
              slotId: { name, parentId },
            };
          }
        }
      };

    const addConditionCreate = (
      state: Draft<ConditionsState>,
      validateCondition: ReturnType<typeof validateAddConditionCreate>,
    ) => {
      const { byId, slotsByParentId, parentIdByChildId } = state;

      const addCondition = ({
        condition: { children, ...condition },
        slotId,
      }: Pick<AddConditionAction["payload"], "condition"> & { slotId: ConditionSlotId }) => {
        validateCondition(condition);
        const addToSlot = (
          { parentId, name = CONDITION_DEFAULT_SLOT_NAME }: ConditionSlotId,
          childIdOrIds: number | number[],
        ) => {
          if (!slotsByParentId[parentId]) {
            slotsByParentId[parentId] = {};
          }
          const existingSlotContents = slotsByParentId[parentId][name];
          const existingChildIds = Array.isArray(existingSlotContents)
            ? existingSlotContents
            : existingSlotContents
              ? [existingSlotContents]
              : undefined;
          const newChildIds = Array.isArray(childIdOrIds) ? childIdOrIds : [childIdOrIds];
          // basic rule is if initial slot value is [] then treat it as an array
          // otherwise assume it's one object to edit
          // at some stage we may be able to infer multi-value slots from manifests
          // but for now a condition builder needs to provide initial children as an array even if [] to support multi-value slots
          if (
            (!existingChildIds && !Array.isArray(childIdOrIds)) ||
            (existingChildIds && !Array.isArray(existingChildIds))
          ) {
            slotsByParentId[parentId][name] = newChildIds[0];
          } else {
            slotsByParentId[parentId][name] = [...(existingChildIds ?? []), ...newChildIds];
          }

          for (const childId of newChildIds) {
            parentIdByChildId[childId] = parentId;
          }
        };

        byId[condition.id] = omit(condition, ["id"]);

        addToSlot(slotId, condition.id);

        for (const [name, childOrChilds] of Object.entries(children ?? {})) {
          if (!childOrChilds) {
            continue;
          }
          const childSlotId: ConditionSlotId = { parentId: condition.id, name };
          if (Array.isArray(childOrChilds)) {
            if (!slotsByParentId[condition.id]?.[name]) {
              // if multi value we need to create the multi-value slot
              addToSlot(childSlotId, []);
            }
            for (const child of childOrChilds) {
              addCondition({ condition: child, slotId: childSlotId });
            }
          } else {
            addCondition({ condition: childOrChilds, slotId: childSlotId });
          }
        }
      };

      return addCondition;
    };

    builder.addCase(addConditionAction, (state, action) => {
      const { to, condition } = action.payload;

      const slotId = "slotId" in to ? to.slotId : CONDITION_ROOT_SLOT_ID;

      const addCondition = addConditionCreate(state, validateAddConditionCreate(state, action));

      addCondition({ condition, slotId });

      if ("responseId" in to) {
        state.idByResponseId[to.responseId] = condition.id;
        state.responseIdByRootConditionId[condition.id] = to.responseId;
      } else if ("triggerId" in to) {
        state.idByTriggerId[to.triggerId] = condition.id;
        state.triggerIdByRootConditionId[condition.id] = to.triggerId;
      }
    });

    builder.addCase(reorderConditionAction, (state, action) => {
      const { id: conditionId, newIndex } = action.payload;
      if (newIndex < 0) {
        throw new InvalidActionError(action, `New index must be greater than or equal to 0. Got ${newIndex}.`);
      }

      const slotIdAndIndex = findSlotIdAndIndexCreate(state)(conditionId);
      if (!slotIdAndIndex) {
        throw new InvalidActionError(action, `Slot not found containing condition with id: ${conditionId}`);
      }
      if (slotIdAndIndex.index === undefined) {
        throw new InvalidActionError(
          action,
          `Slot containing condition with id: ${conditionId} not multi-value for re-ordering.`,
        );
      }
      const {
        slotId: { parentId, name },
        index,
      } = slotIdAndIndex;
      const slotsByName = state.slotsByParentId[parentId];
      const slotName = name || CONDITION_DEFAULT_SLOT_NAME;
      assert(Array.isArray(slotsByName[slotName]));
      slotsByName[slotName] = arrayMove(slotsByName[slotName], index, newIndex);
    });

    builder.addCase(removeConditionAction, (state, action) => {
      const { id: conditionId } = action.payload;
      if (!(conditionId in state.byId)) return;

      removeCreate(state)(conditionId);
    });

    builder.addCase(updateConditionAction, (state, action) => {
      const { id: conditionId, config } = action.payload;
      if (!(conditionId in state.byId))
        throw new InvalidActionError(action, `Condition not found with id ${conditionId}.`);
      state.byId[conditionId].config = config;
    });

    builder.addCase(moveConditionAction, (state, action) => {
      const { id: conditionId, to } = action.payload;

      const { slotsByParentId } = state;

      const fromSlotIdAndIndex = findSlotIdAndIndexCreate(state)(conditionId);
      if (!fromSlotIdAndIndex) {
        throw new InvalidActionError(action, `Slot not found containing condition with id: ${conditionId}`);
      }
      if (fromSlotIdAndIndex.index === undefined) {
        throw new InvalidActionError(
          action,
          `Slot containing condition with id: ${conditionId} not multi-value for re-ordering.`,
        );
      }
      const {
        slotId: { parentId: fromParentId, name: fromName },
        index,
      } = fromSlotIdAndIndex;

      if (!to.slotId || !to.slotId.parentId) {
        throw new InvalidActionError(
          action,
          `Failed to move condition with id: ${conditionId} - no new slot provided.`,
        );
      }
      const { parentId: toParentId, name: toName } = to.slotId;

      if (conditionId === toParentId) {
        throw new InvalidActionError(action, `Condition cannot be moved to inside itself.`);
      }

      const fromSlotsByName = slotsByParentId[fromParentId];
      const fromSlotName = fromName || CONDITION_DEFAULT_SLOT_NAME;
      const fromSlot = fromSlotsByName[fromSlotName] as number[];

      const toSlotsByName = slotsByParentId[toParentId];
      const toSlotName = toName || CONDITION_DEFAULT_SLOT_NAME;
      const toSlot = toSlotsByName[toSlotName] as number[];
      if (!toSlotsByName || !toSlot) {
        throw new InvalidActionError(
          action,
          `Failed to move condition with id: ${conditionId} - specified slot to move to is undefined.`,
        );
      }

      if (!(fromParentId in state.slotsByParentId)) {
        state.slotsByParentId[fromParentId] = {};
      }
      state.slotsByParentId[fromParentId][fromSlotName] = [...fromSlot.slice(0, index), ...fromSlot.slice(index + 1)];
      if (!(toParentId in state.slotsByParentId)) {
        state.slotsByParentId[toParentId] = {};
      }
      state.slotsByParentId[toParentId][toSlotName] =
        to.index && to.index > 0
          ? [...toSlot.slice(0, to.index), conditionId, ...toSlot.slice(to.index)]
          : to.index === 0
            ? [conditionId, ...toSlot]
            : [...toSlot, conditionId];

      state.parentIdByChildId[conditionId] = toParentId;
    });

    builder.addCase(removeResponseAction, (state, action) => {
      const { id: responseId } = action.payload;
      const conditionId = state.idByResponseId[responseId];
      if (!conditionId) return;
      removeCreate(state)(conditionId);
    });

    builder.addCase(removeTriggerAction, (state, action) => {
      const { id: triggerId } = action.payload;
      const conditionId = state.idByTriggerId[triggerId];
      if (!conditionId) return;
      removeCreate(state)(conditionId);
    });

    builder.addCase(removeNodeAction, (state, action) => {
      const { id: nodeId } = action.payload;

      // Remove conditions authored on the deleted node.
      const associatedConditionIds = Object.entries(state.byId)
        .filter(([_, condition]) => condition.nodeId === nodeId)
        .map(([id]) => Number(id));

      const removeCondition = removeCreate(state);
      for (const conditionId of associatedConditionIds) {
        removeCondition(conditionId);
      }

      // Reset any surviving condition's configured values (e.g. a compared-to value)
      // that referenced the deleted node so nothing dangles.
      for (const condition of Object.values(state.byId)) {
        resetNodeValueRefs(condition.config, nodeId);
      }
    });
  });
};

const toIds = (idOrIds: number | number[] | undefined) => (Array.isArray(idOrIds) ? idOrIds : idOrIds ? [idOrIds] : []);

const isId = (c: number | undefined): c is number => !!c;

const removeCreate = ({
  byId,
  slotsByParentId,
  idByResponseId,
  idByTriggerId,
  triggerIdByRootConditionId,
  responseIdByRootConditionId,
  parentIdByChildId,
}: Mutable<ConditionsState>) => {
  const remove = (id: number) => {
    delete byId[id];

    const childIds = Object.values(slotsByParentId[id] ?? {})
      .flatMap(toIds)
      .filter(isId);

    for (const childId of childIds) {
      remove(childId);
    }

    // remove from parent slot
    // dumb algorithm and not fast. use parentIdByChildId lookup like node designs if too slow
    for (const [parentIdStr, slotsByName] of Object.entries(slotsByParentId)) {
      const parentId = Number(parentIdStr);
      for (const [name, idOrIds] of Object.entries(slotsByName)) {
        if (Array.isArray(idOrIds)) {
          if (idOrIds.includes(id)) {
            slotsByParentId[parentId][name] = idOrIds.filter((v) => v !== id);
          }
        } else if (idOrIds === id) {
          delete slotsByParentId[parentId][name];
        }
      }
    }

    delete slotsByParentId[id];
    delete parentIdByChildId[id];

    delete triggerIdByRootConditionId[id];
    delete responseIdByRootConditionId[id];

    for (const [responseIdStr, conditionId] of Object.entries(idByResponseId)) {
      if (conditionId === id) {
        delete idByResponseId[Number(responseIdStr)];
      }
    }

    for (const [responseIdStr, conditionId] of Object.entries(idByTriggerId)) {
      if (conditionId === id) {
        delete idByTriggerId[Number(responseIdStr)];
      }
    }
  };

  return remove;
};
